import { useContext } from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';

import {
  Avatar,
  Button,
  defaultThemeProp,
  Flex,
  Text,
  Progress,
  StyledFlex,
  useI18n
} from '@pega/cosmos-react-core';

import { FeedContext } from './Feed.context';
import FeedButton from './FeedButton';
import type { FeedCustomInputRegionProps } from './Feed.types';

const StyledAvatar = styled(Avatar)(({ theme }) => {
  return css`
    margin-block-start: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledAvatar.defaultProps = defaultThemeProp;

const StyledContent = styled.div`
  width: 100%;

  & > ${StyledFlex}:first-of-type {
    position: relative;
  }
`;

export const StyledActions = styled.div`
  ${FeedButton} + ${FeedButton} {
    margin-inline-start: unset;
  }
`;

const FeedCustomInputRegion: FC<FeedCustomInputRegionProps> = ({
  variant,
  id,
  banners,
  progress,
  content,
  onSubmit,
  onCancel,
  recipients,
  postType,
  postTypeMenu,
  visibility,
  visibilityControl
}: FeedCustomInputRegionProps) => {
  const t = useI18n();
  const { avatarSrc, fullname, username, userStatus } = useContext(FeedContext).userInfo;

  const inputLabel = postType?.label || t('feed_new_post');

  return (
    <Flex container={{ alignItems: 'start' }}>
      <StyledAvatar
        name={fullname}
        size={variant === 'post' ? 'l' : 'm'}
        imageSrc={avatarSrc}
        status={userStatus}
      />

      <Flex as={StyledContent} container={{ direction: 'column', gap: 2, pad: [0.5, 4, 0.5, 1] }}>
        <Flex container={{ direction: 'column', gap: 0.5 }} inert={progress ? '' : undefined}>
          {postTypeMenu || <Text variant='h3'>{inputLabel}</Text>}
          {banners}
          {visibilityControl}
          {content}

          <Progress
            visible={!!progress}
            focusOnVisible
            placement='local'
            message={typeof progress === 'string' ? progress : undefined}
          />
        </Flex>

        {/* Replace this section with ResponsiveActions component once merged. */}
        <Flex
          as={StyledActions}
          container={{ justify: 'between', alignItems: 'center', wrap: 'wrap', gap: 1 }}
        >
          <Button disabled={!!progress} onClick={() => onCancel(id)}>
            {t('cancel')}
          </Button>

          <Button
            variant='primary'
            disabled={!!progress}
            onClick={() =>
              onSubmit({
                id,
                username,
                postType,
                visibility,
                recipients: variant === 'post' ? recipients : undefined
              })
            }
          >
            {t('submit')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FeedCustomInputRegion;
